import { Injector, inject, runInInjectionContext } from '@angular/core';

import { Actions, FunctionalEffect, createEffect, ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';
import { isObservable, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { generateUuid } from '@recruitee/utils';

import { DEFAULT_SCOPE_NAME } from '../entity-collection/types';
import { ActionsFromConfig, ConfigObject, EntityActionCreator } from '../types';
import {
  CustomEffectsConfig,
  EffectHandler,
  FunctionalEffectHandler,
  RecognizedAction,
} from './types';

/* eslint-disable max-lines-per-function */

type InferObservable<T> = T extends Observable<infer R> ? R : never;

const transferActionPayload = <
  T extends ReturnType<
    EntityActionCreator<any, { data?: any; correlationId?: string; scope?: string }>
  >,
  R extends InferObservable<ReturnType<EffectHandler<any, any, any>>>,
>(
  actionName: RecognizedAction | string,
  effectAction: T,
  payload: R,
) => {
  switch (actionName) {
    case RecognizedAction.createOneOptimistic:
    case RecognizedAction.createOnePessimistic:
      return {
        ...payload,
        correlationId: effectAction.payload.correlationId || generateUuid(),
        scope: effectAction.payload.scope || DEFAULT_SCOPE_NAME,
      };

    case RecognizedAction.updateOneOptimistic:
      return {
        ...payload,
        correlationId: effectAction.payload.correlationId || generateUuid(),
      };

    case RecognizedAction.updateOnePessimistic:
      return {
        ...(effectAction.payload.data && { data: effectAction.payload.data }),
        ...payload,
      };

    case RecognizedAction.fetch:
    case RecognizedAction.update:
      return payload;

    case RecognizedAction.fetchOne:
      const isError = 'error' in payload;
      return isError ? { ...payload, ...effectAction.payload } : payload;

    default:
      return {
        scope: effectAction.payload?.scope || DEFAULT_SCOPE_NAME,
        ...payload,
      };
  }
};

export const customActionCreator = <A extends TypedAction<any> & { payload?: P }, P = any>(
  action: A,
): EntityActionCreator<any, any> => {
  const creator = function () {
    return action;
  };

  creator.type = action.type;
  creator.actionName = action.type;
  creator.namespace = '(global)';

  return creator;
};

/**
 * This function creates a wrapper around ngrx createEffect that reduces
 * boilerplate for known actions {@link RecognizedAction}
 *
 * @example
 * private effect = createEffectFactory(actions, actions$);
 *
 * public fetchOne$ = this.effect(actions.fetchOne, () => this.httpService.fetchOne().pipe(
 *   map(data => ({ data }))
 * ))
 *
 * is equalTo
 *
 * public fetchOne$ = createEffect(() => this.actions$.pipe(
 *   ofType(actions.fetchOne),
 *   switchMap(({payload}) => this.httpService.fetchOne().pipe(
 *     map(data => actions.fetchOneSuccess({ payload: { data } })),
 *     catchError(error => actions.fetchOneFailed({ payload: { error } }))
 *   ))
 * ))
 *
 * to check how payload is transferred to Success or Failed action see {@link transferActionPayload}
 */
export const createEffectFactory =
  <EntityActions extends ActionsFromConfig<ConfigObject<any>>>(
    actions: EntityActions,
    actions$: Actions,
  ) =>
  <
    ActionCreator extends EntityActionCreator<any, any>,
    Config extends CustomEffectsConfig & { functional?: false },
    Handler extends EffectHandler<EntityActions, ActionCreator, Config>,
  >(
    actionCreator: ActionCreator,
    effect: Handler,
    opts?: Config,
  ) => {
    const mapOperator = opts?.customMapOperator ? opts.customMapOperator : switchMap;

    // TODO
    // @ts-ignore
    return createEffect(
      () =>
        actions$.pipe(
          ofType(actionCreator.type),
          mapOperator((effectAction: ReturnType<ActionCreator>) => {
            if (actionCreator.actionName in RecognizedAction) {
              const knownEffect$ = <Observable<ReturnType<Handler>>>effect(effectAction);
              return knownEffect$.pipe(
                map(payload =>
                  actions[`${actionCreator.actionName}Success`]({
                    payload: transferActionPayload<any, ReturnType<Handler>>(
                      actionCreator.actionName,
                      effectAction,
                      payload,
                    ),
                  }),
                ),
                catchError(error =>
                  of(
                    actions[`${actionCreator.actionName}Failed`]({
                      payload: transferActionPayload<any, any>(
                        actionCreator.actionName,
                        effectAction,
                        { error },
                      ),
                    }),
                  ),
                ),
              );
            }

            const result = <ReturnType<typeof effect>>effect(effectAction);
            return isObservable(result) ? result : of(result);
          }),
        ),
      opts,
    );
  };

/**
 * This function creates a wrapper around ngrx createEffect that reduces
 * boilerplate for known actions {@link RecognizedAction} and allows to pass additional injected props
 * to all effect handlers
 *
 * @param additionalProps - function that returns object with additional props. Use for injecting deps,
 * eg.(httpService = inject(HttpService), i18n = inject(I18nService)) => ({httpService, i18n}))
 * all effects created with a factory will then have access to httpService and i18n on first argument
 *
 * @example
 * private effect = createFunctionalEffectFactory(actions, (httpService) => ({httpService}))
 *
 * public fetchOne$ = effect(actions.fetchOne, ({httpService}) => httpService.fetchOne().pipe(
 *   map(data => ({ data }))
 * ))
 *
 * is equalTo
 *
 * const fetchOne = createEffect((actions$ = inject(Actions), httpService = inject(HttpService)) => actions$.pipe(
 *   ofType(actions.fetchOne),
 *   switchMap(({payload}) => httpService.fetchOne().pipe(
 *     map(data => actions.fetchOneSuccess({ payload: { data } })),
 *     catchError(error => actions.fetchOneFailed({ payload: { error } }))
 *   ))
 * ), {
 *  functional: true
 * })
 *
 * to check how payload is transferred to Success or Failed action see {@link transferActionPayload}
 *
 * @example
 * Factory can also be used as a shortcut to inject many dependencies to multiple functional effect
 *
 * // empty actions as first argument
 * private effect = createFunctionalEffectFactory({},
 * // multiple injections as second argument
 *   (http = inject(HttpService), i18n = inject(I18nService)) => ({http, i18n});
 *
 * // action is also always available in the dictionary of the first argument
 * const fetchOne = effect(actions.fetchOne, ({http, i18n, action}) => {
 *   if(action.payload.id) {
 *     return http.fetchOne(action.payload.id).pipe(
 *       map(data => ({ data }))
 * )
 * ...
 * }
 *
 * // You can also inject other services after the first argument
 * const otherEffect = effect(actions.other, ({}, myService = inject(myService), i18n = inject(I18nService)) => {
 *   ...
 * })
 *
 */
export const createFunctionalEffectFactory =
  <
    EntityActions extends ActionsFromConfig<ConfigObject<any>>,
    AdditionalProps extends () => Record<string, any> = () => Record<string, any>,
  >(
    actions: EntityActions,
    additionalProps?: AdditionalProps,
  ) =>
  <
    ActionCreator extends EntityActionCreator<any, any>,
    Config extends Omit<CustomEffectsConfig, 'functional'>,
    Handler extends FunctionalEffectHandler<
      EntityActions,
      ActionCreator,
      Config,
      ReturnType<AdditionalProps>
    >,
  >(
    actionCreator: ActionCreator,
    effect: Handler,
    opts?: Config,
  ) => {
    const mapOperator = opts?.customMapOperator ? opts.customMapOperator : switchMap;

    // @ts-ignore
    return <FunctionalEffect>createEffect(
      (actions$ = inject(Actions)) => {
        const handlerArguments = <ReturnType<AdditionalProps>>(
          (additionalProps ? additionalProps() : {})
        );
        const injector = inject(Injector);
        return actions$.pipe(
          ofType(actionCreator.type),
          mapOperator((effectAction: ReturnType<ActionCreator>) => {
            if (actionCreator.actionName in RecognizedAction) {
              const knownEffect$ = runInInjectionContext(
                injector,
                () => <Observable<ReturnType<Handler>>>effect({
                    action: effectAction,
                    ...handlerArguments,
                  }),
              );
              return knownEffect$.pipe(
                map(payload =>
                  actions[`${actionCreator.actionName}Success`]({
                    payload: transferActionPayload<any, ReturnType<Handler>>(
                      actionCreator.actionName,
                      effectAction,
                      payload,
                    ),
                  }),
                ),
                catchError(error =>
                  of(
                    actions[`${actionCreator.actionName}Failed`]({
                      payload: transferActionPayload<any, any>(
                        actionCreator.actionName,
                        effectAction,
                        { error },
                      ),
                    }),
                  ),
                ),
              );
            }

            const result = runInInjectionContext(
              injector,
              () => <ReturnType<typeof effect>>effect({
                  action: effectAction,
                  ...handlerArguments,
                }),
            );
            return isObservable(result) ? result : of(result);
          }),
        );
      },
      {
        ...opts,
        functional: true,
      },
    );
  };
